import React, { useState, useEffect } from 'react';
import { Modal, Form, Space, Tag } from 'antd';
import { useT } from 'hooks/Locale/useT';
import { renderFilterField } from './helper';
import { ModuleInterface } from './types';
import { Button, Search } from 'ui';
import dayjs from 'dayjs';
import { config } from 'config/config';
import { Input } from 'components/Form';
import { ICONS } from 'config/icons';

export interface FilterState {
    filterBy?: 'CUSTOMER' | 'ORGANIZATION' | 'PROJECT' | string;
    customer?: string | number;
    organization?: string | number;
    projectId?: string | number;
    taskId?: string | number;
    id?: string | number;
    [key: string]: any;
}

interface FilterInterface {
    modules: ModuleInterface[];
    filter?: FilterState;
    /** Applied once on mount (form values + applied tags). */
    initialValues?: FilterState;
    setFilter: React.Dispatch<React.SetStateAction<any>>;
    title?: string;
    okProp?: string;
    resetFields?: Record<string, string[]>;
    submit?: (normalized: any) => void;
    hasSearch?: boolean;
    searchKey?: string;
}

const Filter: React.FC<FilterInterface> = ({
    modules,
    filter,
    initialValues,
    setFilter,
    title = 'general.filter',
    okProp = 'general.filter',
    resetFields,
    submit,
    searchKey = 'q',
    hasSearch = false,
}) => {
    const t = useT();
    const [showFilter, setShowFilter] = useState(false);
    const [form] = Form.useForm();
    const [appliedDisplay, setAppliedDisplay] = useState<any>({});

    useEffect(() => {
        if (initialValues) {
            form.setFieldsValue(initialValues);
            setAppliedDisplay(initialValues);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (filter) {
            form.setFieldsValue(filter);
            setAppliedDisplay(filter);
        }
    }, [filter, form]);

    const normalize = (values: any) => {
        return Object.fromEntries(
            Object.entries(values).map(([key, val]: any) => {
                if (val && typeof val === 'object' && 'value' in val) {
                    return [key, val.value];
                }
                if (Array.isArray(val) && val.length && val[0]?.value !== undefined) {
                    return [key, val.map((v) => v.value)];
                }
                return [key, val];
            })
        );
    };

    const handleChange = (el: any) => {
        const values = normalize(form.getFieldsValue());
        setFilter(values);

        onChangeClear(el);
    };

    const onChangeClear = (changedField: any) => {
        if (!resetFields) return;
        const fieldName = Object.keys(changedField)[0];

        const fieldsToClear = resetFields[fieldName];
        if (fieldsToClear?.length) {
            fieldsToClear.forEach((field) => {
                form.setFieldValue(field, undefined);
            });

            setAppliedDisplay((prev: any) => {
                const updated = { ...prev };
                fieldsToClear.forEach((f) => delete updated[f]);
                return updated;
            });
        }
    };

    const submitForm = (values: any) => {
        const normalized = normalize(values);
        setAppliedDisplay(values);
        setShowFilter(false);

        if (submit) {
            submit(normalized);
        }
    };

    return (
        <>
            <Modal
                className="user-list-modal"
                open={showFilter}
                title={t(title)}
                onOk={() => form.submit()}
                width={800}
                onCancel={() => setShowFilter(false)}
                okText={t(okProp)}
                cancelText={t('general.cancel')}
            >
                <Form
                    form={form}
                    onFinish={submitForm}
                    onValuesChange={handleChange}
                    className={'table-filter-modal'}
                >
                    {modules.map((module) => (
                        <React.Fragment key={`${module.name}`}>
                            {renderFilterField(module)}
                        </React.Fragment>
                    ))}
                    {hasSearch && (
                        <span className={'hidden'}>
                            <Input name={searchKey} />
                        </span>
                    )}
                </Form>
            </Modal>

            <div className="flex justify-between">
                <Space wrap style={{ marginBottom: 16 }}>
                    {modules.map((module) => {
                        const value = appliedDisplay[module.name];
                        if (!value || (Array.isArray(value) && value.length === 0)) return null;

                        let displayValue = '';

                        if (module.type === 'dateRange' && Array.isArray(value)) {
                            const [start, end] = value;
                            displayValue = `${dayjs(start).format(
                                config.defaultDateFormat
                            )} - ${dayjs(end).format(config.defaultDateFormat)}`;
                        } else if (module.type === 'asyncSelect') {
                            // Same rule as ui/Select: a module with `translation` shows t('<prefix>.<label>').
                            const label = (v: any) => {
                                const raw = v?.label ?? v?.value ?? v;
                                return module.translation
                                    ? t(`${module.translation}.${String(raw).toLowerCase()}`, String(raw))
                                    : raw;
                            };
                            if (Array.isArray(value)) {
                                displayValue = value.map(label).join(', ');
                            } else if (typeof value === 'object' && value?.label) {
                                displayValue = label(value);
                            } else {
                                displayValue = String(value);
                            }
                        } else if (
                            (module.type === 'select' ||
                                module.type === 'switch' ||
                                module.type === 'checkbox') &&
                            module.manualOptions
                        ) {
                            const mapValue = (v: any) =>
                                t(module.manualOptions![String(v)] ?? String(v));
                            displayValue = Array.isArray(value)
                                ? value.map(mapValue).join(', ')
                                : mapValue(value);
                        } else {
                            displayValue = Array.isArray(value) ? value.join(', ') : String(value);
                        }

                        return (
                            <Tag
                                key={module.name}
                                closable={!module.required}
                                onClose={(e) => {
                                    e.preventDefault();

                                    form.setFieldValue(module.name, undefined);

                                    const rawValues = form.getFieldsValue();
                                    const normalized = normalize(rawValues);

                                    setAppliedDisplay(rawValues);

                                    setFilter(normalized);

                                    if (submit) {
                                        submit(normalized);
                                    }
                                }}
                            >
                                {t(module.label)}: {displayValue}
                            </Tag>
                        );
                    })}
                </Space>

                <Space>
                    <Button
                        type="primary"
                        onClick={() => setShowFilter(true)}
                        icon={ICONS.FILTER}
                        message="general.filter"
                    />
                    {hasSearch && (
                        <Search
                            setSearchString={(search) => {
                                form.setFieldValue(searchKey, search || undefined);

                                const formValues = form.getFieldsValue();
                                const normalized = normalize(formValues);

                                if (search) {
                                    normalized[searchKey] = search;
                                } else {
                                    delete normalized[searchKey];
                                }

                                setFilter(normalized);

                                if (submit) submit(normalized);
                            }}
                        />
                    )}
                </Space>
            </div>
        </>
    );
};

export default Filter;
